import { toast } from "react-toastify";
import FileSaver from "file-saver";
import { SORT_TYPE } from "Constants/constant";
import {
  fetchReportExportThreshold,
  queueReportExport,
} from "store/services/reportExport.services";
import { apiPostRequest } from "helpers/Requests";
import { sanitizeDocumentUrl } from "Utils/utils";
import {
  ReportExportButton,
  ReportFilterActions,
  ReportFilterActionsBar,
  ReportFilterField,
  ReportFilterGrid,
  ReportFilterSearchField,
  ReportFilterTextField,
  ReportFiltersShell,
  ReportPageContent,
  ReportTableSection,
  useReportSearchOnType,
  REPORT_SEARCH_MIN_LENGTH,
  REPORT_SEARCH_DEBOUNCE_MS,
} from "./reportFilterUi";

const DEFAULT_THRESHOLD = 300;

/**
 * Resolve export row count for async vs sync decision.
 * Returns null when count is unknown (prefer background export).
 */
export const resolveExportRowCount = (totalRows, fallbackTotal) => {
  const candidates = [totalRows, fallbackTotal];

  for (const value of candidates) {
    const parsed =
      typeof value === "number" ? value : parseInt(value, 10);
    if (!Number.isNaN(parsed) && parsed > 0) {
      return parsed;
    }
  }

  return null;
};

export const shouldQueueReportExport = (rowCount, threshold) => {
  if (rowCount === null) {
    return true;
  }

  return rowCount >= threshold;
};

/**
 * Resolve active table sort for API requests (list + export).
 */
export const resolveReportSort = (
  filters = {},
  sortBy,
  sortDescending
) => {
  const sortField = filters?.sort_field ?? sortBy;
  if (!sortField) {
    return {};
  }

  const sortOrder =
    filters?.sort_order ??
    (sortDescending ? SORT_TYPE.DESC : SORT_TYPE.ASC);

  return {
    sort_field: sortField,
    sort_order: sortOrder,
  };
};

export const withReportSortOnUrl = (url, filters, sortBy, sortDescending) => {
  const sort = resolveReportSort(filters, sortBy, sortDescending);
  if (!sort.sort_field || !url || url.includes("sort_field=")) {
    return url;
  }

  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}sort_field=${encodeURIComponent(
    sort.sort_field
  )}&sort_order=${encodeURIComponent(sort.sort_order)}`;
};

export const withReportSortOnPayload = (
  payload,
  filters,
  sortBy,
  sortDescending
) => ({
  ...payload,
  ...resolveReportSort(filters, sortBy, sortDescending),
});

let cachedThreshold = null;

export const REPORT_TYPE_LABELS = {
  participant_report: "Participant List Report",
  worker_report: "Worker List Report",
  roc_report: "ROC Report",
  roc_weekly_report: "ROC Weekly Report",
  timesheet_report: "Timesheet Report",
  xero_timesheet_report: "Payroll Report",
  casenote_report: "Case Note Report",
  worker_documents_report: "Worker Expiring Documents",
  participant_documents_report: "Participant Expiring Documents",
  kyp_report: "KYP Report",
  risk_assessment_report: "Risk Assessment Report",
  feedback_report: "Feedback Report",
  complaint_report: "Complaint Report",
  cos_case_report: "COS Case Note Report",
  cos_funding_report: "COS Funding Report",
  invoice_summary_report: "Invoice Summary Report",
  invoice_detail_report: "Invoice Detail Report",
  incident_report: "Incident Report",
  incident_management_report: "Incident Management Report",
  ndis_claim_report: "NDIS Claims Report",
};

export const getLargeExportThreshold = async () => {
  if (cachedThreshold !== null) {
    return cachedThreshold;
  }

  try {
    const res = await fetchReportExportThreshold();
    cachedThreshold =
      res?.data?.data?.large_export_threshold ?? DEFAULT_THRESHOLD;
  } catch {
    cachedThreshold = DEFAULT_THRESHOLD;
  }

  return cachedThreshold;
};

/**
 * Download a report export file (PDF/XLSX) instead of opening it in a new tab.
 */
export const downloadReportExportFile = async (
  url,
  {
    format = "pdf",
    report_label: reportLabel = "report",
    showToast = true,
  } = {}
) => {
  if (!url) {
    const message = "Download link is not available.";
    if (showToast) {
      toast.error(message);
    }
    const error = new Error(message);
    error.downloadHandled = true;
    throw error;
  }

  const ext = format === "xlsx" ? "xlsx" : "pdf";
  const safeName =
    String(reportLabel)
      .replace(/[^\w\s-]/g, "")
      .trim()
      .replace(/\s+/g, "_")
      .slice(0, 80) || "report";
  const fileUrl = sanitizeDocumentUrl(url);
  const urlExt = fileUrl.match(/\.(xlsx|csv|pdf)(?:\?|$)/i)?.[1]?.toLowerCase();
  const resolvedExt =
    urlExt && (format === "xlsx" ? ["xlsx", "csv"] : ["pdf"]).includes(urlExt)
      ? urlExt
      : ext;
  const fileName = `${safeName}.${resolvedExt}`;

  let toastId;
  if (showToast) {
    toastId = toast.loading(`Downloading ${fileName}…`);
  }

  try {
    const response = await fetch(fileUrl);
    if (!response.ok) {
      throw new Error(`Download failed (${response.status})`);
    }
    const blob = await response.blob();
    FileSaver.saveAs(blob, fileName);

    if (showToast) {
      toast.update(toastId, {
        render: `${fileName} downloaded successfully.`,
        type: "success",
        isLoading: false,
        autoClose: 4000,
      });
    }

    return { fileName };
  } catch (error) {
    const message =
      error?.message?.includes("Failed to fetch") || error?.name === "TypeError"
        ? "Could not download the file. Please check your connection and try again."
        : error?.message || "Download failed.";

    if (showToast) {
      toast.update(toastId, {
        render: message,
        type: "error",
        isLoading: false,
        autoClose: 5000,
      });
    }

    const downloadError = new Error(message);
    downloadError.downloadHandled = true;
    throw downloadError;
  }
};

/**
 * Unified export handler for reporting pages.
 * Large datasets are queued; small datasets use the existing synchronous flow.
 */
export const executeReportExport = async ({
  reportType,
  reportLabel,
  format,
  payload,
  syncUrl,
  totalRows,
  setLoader,
  onSyncExcel,
  onSyncPdf,
  onQueued,
  sortBy,
  sortDescending,
  filters = {},
  fallbackTotal,
}) => {
  const sortedPayload = withReportSortOnPayload(
    payload,
    filters,
    sortBy,
    sortDescending
  );
  const sortedUrl = withReportSortOnUrl(
    syncUrl,
    filters,
    sortBy,
    sortDescending
  );

  const threshold = await getLargeExportThreshold();
  const rowCount = resolveExportRowCount(totalRows, fallbackTotal);
  const useAsyncQueue = shouldQueueReportExport(rowCount, threshold);

  if (useAsyncQueue) {
    setLoader?.(true);
    try {
      const exportPayload = {
        ...sortedPayload,
        report_type: reportType,
        report_label: reportLabel || REPORT_TYPE_LABELS[reportType] || reportType,
        export_format: format === "xlsx" ? "xlsx" : "pdf",
      };

      const res = await queueReportExport(exportPayload);
      const exportData = res?.data?.data?.export;

      onQueued?.(exportData);
      return { mode: "queued", export: exportData };
    } catch (error) {
      toast.error(
        typeof error === "string" ? error : "Failed to queue report export."
      );
      throw error;
    } finally {
      setLoader?.(false);
    }
  }

  setLoader?.(true);
  const syncPayload = {
    ...sortedPayload,
    report: format === "xlsx" ? "excel" : format,
  };

  let generateToastId;
  if (format === "pdf") {
    generateToastId = toast.loading("Generating PDF report…");
  }

  try {
    const res = await apiPostRequest(sortedUrl, syncPayload);
    if (generateToastId) {
      toast.dismiss(generateToastId);
      generateToastId = null;
    }
    if (res.status === 200) {
      if (format === "xlsx") {
        await onSyncExcel?.(res?.data?.data);
        toast.success("Excel report downloaded.");
      } else {
        await downloadReportExportFile(res?.data?.data, {
          format: "pdf",
          report_label: reportLabel,
        });
      }
      return { mode: "sync", data: res?.data?.data };
    }
  } catch (error) {
    if (generateToastId) {
      toast.dismiss(generateToastId);
    }
    if (!error?.downloadHandled) {
      toast.error(typeof error === "string" ? error : "Export failed.");
    }
    throw error;
  } finally {
    setLoader?.(false);
  }

  return { mode: "sync" };
};

export const formatRemainingTime = (seconds) => {
  if (seconds === null || seconds === undefined) {
    return "Calculating…";
  }
  if (seconds <= 0) {
    return "Almost ready";
  }
  if (seconds < 60) {
    return `~${seconds}s remaining`;
  }
  const mins = Math.ceil(seconds / 60);
  return mins === 1 ? "~1 min remaining" : `~${mins} mins remaining`;
};

export const formatEstimatedDuration = (seconds) => {
  if (!seconds) return "a few moments";
  if (seconds < 60) return `about ${seconds} seconds`;
  const mins = Math.ceil(seconds / 60);
  return mins === 1 ? "about 1 minute" : `about ${mins} minutes`;
};

export const getExportStatusLabel = (status) => {
  const labels = {
    queued: "Queued",
    processing: "Processing",
    completed: "Ready to download",
    failed: "Failed",
  };
  return labels[status] || status;
};

export const getExportStatusColor = (status) => {
  const colors = {
    queued: "bg-amber-100 text-amber-800",
    processing: "bg-blue-100 text-blue-800",
    completed: "bg-green-100 text-green-800",
    failed: "bg-red-100 text-red-800",
  };
  return colors[status] || "bg-gray-100 text-gray-800";
};

export {
  ReportExportButton,
  ReportFilterActions,
  ReportFilterActionsBar,
  ReportFilterField,
  ReportFilterGrid,
  ReportFilterSearchField,
  ReportFilterTextField,
  ReportFiltersShell,
  ReportPageContent,
  ReportTableSection,
  useReportSearchOnType,
  REPORT_SEARCH_MIN_LENGTH,
  REPORT_SEARCH_DEBOUNCE_MS,
};
